// Lightweight, non-blocking storefront analytics.
// Events are best-effort: failures never affect the user experience.
// Also mirrors key events to the Meta (Facebook) Pixel when available.

const API_BASE_URL = 'https://backend-deeps-projects-1a2428cf.vercel.app/api';
const TRACK_ENDPOINT = `${API_BASE_URL}/analytics/track`;
const SESSION_KEY = 'bxl_session_id';
// Meta Events Manager test code — clear this string after testing is done
const META_PIXEL_TEST_EVENT_CODE = 'TEST41437';

const getSessionId = () => {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (e) {
    return null;
  }
};

const fbqTrack = (eventName, params) => {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      const eventData = META_PIXEL_TEST_EVENT_CODE
        ? { test_event_code: META_PIXEL_TEST_EVENT_CODE }
        : undefined;
      const payload = params || {};
      if (eventData) {
        window.fbq('track', eventName, payload, eventData);
      } else if (params) {
        window.fbq('track', eventName, params);
      } else {
        window.fbq('track', eventName);
      }
    }
  } catch (e) {
    // Never let pixel errors break the app
  }
};

/**
 * Fire-and-forget event tracking.
 * @param {string} type - one of page_view, product_view, product_click, add_to_cart,
 *                        remove_from_cart, begin_checkout, add_to_wishlist, search, purchase
 * @param {object} payload - { product_id, product_name, collection_id, value, quantity, meta }
 */
export const trackEvent = (type, payload = {}) => {
  if (!type) return;

  const body = {
    type,
    session_id: getSessionId(),
    path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    ...payload,
  };

  try {
    const json = JSON.stringify(body);

    // Note: sendBeacon is intentionally NOT used here. Beacons always send
    // credentials (mode "include"), which fails CORS preflight against our
    // API (supports_credentials=False). fetch + keepalive survives page
    // navigation just like a beacon, but lets us omit credentials.
    fetch(TRACK_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch (e) {
    // Swallow — tracking must never throw into the app.
  }
};

export const trackPageView = (path) => {
  trackEvent('page_view', { path });
  // SPA route changes need an explicit PageView for Meta Pixel
  fbqTrack('PageView');
};

export const trackProductView = (product) => {
  trackEvent('product_view', {
    product_id: product?.id || product?._id,
    product_name: product?.name,
    collection_id: product?.collection_id || product?.collectionId,
    value: product?.price,
  });
  fbqTrack('ViewContent', {
    content_ids: [String(product?.id || product?._id || '')].filter(Boolean),
    content_name: product?.name,
    content_type: 'product',
    value: Number(product?.price) || 0,
    currency: 'INR',
  });
};

export const trackProductClick = (product) =>
  trackEvent('product_click', {
    product_id: product?.id || product?._id,
    product_name: product?.name,
  });

export const trackAddToCart = (product, quantity = 1, size) => {
  const value = (Number(product?.price) || 0) * quantity;
  trackEvent('add_to_cart', {
    product_id: product?.id || product?._id,
    product_name: product?.name,
    quantity,
    value,
    meta: size ? { size } : {},
  });
  fbqTrack('AddToCart', {
    content_ids: [String(product?.id || product?._id || '')].filter(Boolean),
    content_name: product?.name,
    content_type: 'product',
    value,
    currency: 'INR',
  });
};

export const trackBeginCheckout = (items = [], total = 0) => {
  trackEvent('begin_checkout', {
    value: total,
    quantity: items.reduce((sum, i) => sum + (Number(i.quantity) || 1), 0),
    meta: { item_count: items.length },
  });
  fbqTrack('InitiateCheckout', {
    content_ids: items
      .map((i) => String(i.product_id || i.id || i._id || ''))
      .filter(Boolean),
    num_items: items.reduce((sum, i) => sum + (Number(i.quantity) || 1), 0),
    value: Number(total) || 0,
    currency: 'INR',
  });
};

export const trackAddToWishlist = (product) => {
  trackEvent('add_to_wishlist', {
    product_id: product?.id || product?._id,
    product_name: product?.name,
  });
  fbqTrack('AddToWishlist', {
    content_ids: [String(product?.id || product?._id || '')].filter(Boolean),
    content_name: product?.name,
    content_type: 'product',
    value: Number(product?.price) || 0,
    currency: 'INR',
  });
};

export const trackSearch = (query) => {
  trackEvent('search', { meta: { query } });
  if (query) {
    fbqTrack('Search', { search_string: String(query) });
  }
};

export const trackPurchase = ({ orderId, value = 0, items = [] } = {}) => {
  trackEvent('purchase', {
    value,
    quantity: items.reduce((sum, i) => sum + (Number(i.quantity) || 1), 0),
    meta: { order_id: orderId, item_count: items.length },
  });
  fbqTrack('Purchase', {
    content_ids: items
      .map((i) => String(i.product_id || i.id || i._id || ''))
      .filter(Boolean),
    content_type: 'product',
    num_items: items.reduce((sum, i) => sum + (Number(i.quantity) || 1), 0),
    value: Number(value) || 0,
    currency: 'INR',
  });
};

const analytics = {
  trackEvent,
  trackPageView,
  trackProductView,
  trackProductClick,
  trackAddToCart,
  trackBeginCheckout,
  trackAddToWishlist,
  trackSearch,
  trackPurchase,
};

export default analytics;
